// src/activities/wvd/WVDParticipate.jsx

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchSamples from '../../hooks/useFetchSamples'; 
import CAPEVRForm from '../cape-vr/cape-vr-views/CAPEVRForm';
import CAPEVRRatings from '../cape-vr/cape-vr-views/CAPEVRRatings';
import FullFeedback from '../../components/feedback/FullFeedback';
import CitationsPanel from '../../components/citations/CitationsPanel';
import useVoiceCitations from '../../hooks/useVoiceCitations';
import { HANDMADE_EVALUATION_TOOL_IDS } from '../../constants/handmadeEvaluationTools';
import {
  PVQD_DATABASE_ID,
  PVQD_70DB_COLLECTION_ID,
} from '../../components/citations/voiceCitationResources';
import NormalizedResponseFeedbackGate from '../../components/application/NormalizedResponseFeedbackGate';
import CapeVrNormalizedFeedback from '../../components/feedback/CapeVrNormalizedFeedback';
import { detectInstrumentResponseFormat } from '../../utils/instrumentResponsePresentation';

function WVDParticipate() {
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const pvqd70dBCollectionId = PVQD_70DB_COLLECTION_ID;

  // Utilitzem el hook per obtenir les mostres actives
  const { samples, loading, error } = useFetchSamples(pvqd70dBCollectionId, apiUrl);
  const { voiceDatabase, voiceCollection } = useVoiceCitations(apiUrl, PVQD_DATABASE_ID, pvqd70dBCollectionId);

  const [currentStep, setCurrentStep] = useState("loading"); // "loading", "form", "ratings", "feedback", "error"
  const [selectedSample, setSelectedSample] = useState(null);
  const [submittedData, setSubmittedData] = useState(null);
  const submittedFormat = detectInstrumentResponseFormat(submittedData);

  // Quan les mostres ja estiguin carregades, tria una mostra aleatòria
  useEffect(() => {
    if (!loading) {
      if (samples.length > 0) {
        const randomSample = samples[Math.floor(Math.random() * samples.length)];
        setSelectedSample(randomSample);
        setCurrentStep("form");
      } else if (error) {
        // Esperem 500 ms per assegurar-nos que no és un error temporal
        const timer = setTimeout(() => {
          setCurrentStep("error");
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, samples, error]);

  const handleSubmit = (data) => {
    if (data?.goBack) {
      setSelectedSample(null);
      setCurrentStep("loading");
    } else {
      setSubmittedData(data);
      setCurrentStep("ratings");
    }
  };

  return (
    <div className="app-module" style={{ padding: '16px', textAlign: 'center' }}>
      <img src="/images/email/common/wvd-event-newsletter-banner-2logos.png" alt="WVD Icon" style={{ maxWidth: '400px' }} />
      <h2 style={{ color: '#ac1e1a' }}>{t('global_perceptual_voice_evaluation_marathon')}</h2>

      {currentStep === "loading" && <p>{t('loading')}</p>}
      {currentStep === "error" && <p style={{ color: 'red' }}>{t('error_fetching_samples')}: {error}</p>}

      {/* Formulari d'avaluació */}
      {currentStep === "form" && selectedSample && (
        <CAPEVRForm
          apiUrl={apiUrl}
          selectedSample={selectedSample}
          sampleDatabase="waldenPVQD"
          onSubmit={handleSubmit}
        />
      )}

      {/* Resultats d'avaluació */}
      {currentStep === "ratings" && (
        <CAPEVRRatings
          submittedData={submittedData}
          sampleDatabase="waldenPVQD"
          onGoToFeedback={() => setCurrentStep("feedback")}
          onRestart={() => setCurrentStep("loading")}
        />
      )}

      {/* Feedback */}
      {currentStep === "feedback" && (
        <NormalizedResponseFeedbackGate assessment={submittedData} provisionalProtocols={['cape-vr']}>
          {submittedFormat.kind === 'normalized' && submittedFormat.protocolKey === 'cape-vr' ? (
            <CapeVrNormalizedFeedback
              apiUrl={apiUrl}
              selectedSample={selectedSample}
              userEvaluation={submittedData}
              onGoToPreview={() => setCurrentStep("loading")}
              contextLabel={t('global_perceptual_voice_evaluation_marathon')}
            />
          ) : (
            <FullFeedback
              apiUrl={apiUrl}
              selectedSample={selectedSample}
              userEvaluation={submittedData}
              onGoToForm={() => setCurrentStep("loading")}
              isWVD={true}
              feedbackEvaluationToolId={HANDMADE_EVALUATION_TOOL_IDS.CAPE_VR}
            />
          )}
        </NormalizedResponseFeedbackGate>
      )}

      <br />

      <CitationsPanel
        toolName="CAPE-Vr"
        voiceDatabase={voiceDatabase}
        voiceCollection={voiceCollection}
      />
    </div>
  );
}

export default WVDParticipate;
